// `evidence/source` — the static source-catalogue read, carved from
// `src/hooks/query/useQuerySources.ts` (now a pure re-export delegate).
//
// WHY IT IS HERE AT ALL, given that the lane declined it once. The previous
// slice recorded this file as a ROUTE ruling rather than a deferral: its
// endpoint is `GET /chat/sources` and its cache identity is `chat_sources`, so
// both prongs of the resource test pointed at chat, exactly as they did for
// `useBlockedDomains`. That reasoning was sound and it is not what changed.
// What changed is that the ruling's own falsifier fired — it said in as many
// words that "a chat ruling that an evidence-consumed `/chat/*` read is
// evidence's moves both files at once" — and the program owner has now ruled
// that evidence owns this data layer and the chat side takes the inversion.
// So this carve is the recorded falsifier being honoured, not overturned.
//
// AND THE FALSIFIER REACHES THE SIBLING TOO, which is stated here rather than
// left for someone to notice. `src/hooks/query/useBlockedDomains.ts` is the same
// shape — `GET /chat/blocked-domains`, identity `chat_blocked_domains` — and its
// consumers are not chat's: all five are `src/components/library/source-lists/**`,
// the evidence source pickers, exactly as this read's were. So the ruling that
// released this file releases that one, and the only reason it is not here is
// scope: this slice is the file that had a CHAT COUPLING to invert, and the
// blocklist has none to invert. It is a named remaining wait on the family
// record, not a distinction on the merits.
//
// The route and the identity BOTH keep their `chat` spelling — see
// `contracts/queryKeys.ts`. Re-minting either to match the new owner would be a
// cold start for every source picker, and the owner ruling is about who owns
// the code, not about what the backend serves it at.
//
// S1: transport through the `@heidi/api/client` registry rather than the
// `@/utils/scribeHTTP` module singleton — the same instance, so the request is
// unchanged.
//
// S-enabled: THERE IS NO USER GATE, and that absence is the behaviour rather
// than an omission. The pre-move hook passed no `enabled`, read no token and
// consulted no flag: the catalogue is user-independent, so it fetches on first
// mount of any source picker and every caller shares one cache entry.
//
// THE ONE TERM IT TAKES IS THE SURFACE, and it is not a caller's to pass. On an
// on-prem build ml-scribe does not mount `GET /chat/sources` — the catalogue is
// a list of WEB domains, and an appliance answers from a local corpus — so the
// read has to be closable, and a package may not read the build profile (F5).
// It arrives exactly as it does for `useQuerySessionChats` in `@heidi/session`:
// an `enabled` that defaults OPEN here and is closed only by the app-side
// delegate (`src/hooks/query/useQuerySources.ts`), which is the hook's sole
// importer outside this package — `src/utils/backendSurfaces.guard.test.ts`
// pins that, so no picker can reach past the delegate and reopen it.
import { useQuery } from '@tanstack/react-query';

import { GET_SOURCES, type ChatSourceItem } from '../../contracts';
import { getSources } from '../api/sources';

export interface SourcesReadOptions {
  /**
   * The app-resolved SURFACE gate — whether the server this build talks to
   * serves `/chat/sources` at all. Not a per-caller switch: only the delegate
   * passes it. Deliberately not part of the key, so a build that can ask and a
   * render that could not share one cache entry.
   */
  enabled?: boolean;
}

/**
 * Fetches the static source metadata behind `/chat/sources`.
 *
 * Consumed by evidence source selection and by the domain filtering in the
 * source-control popover.
 *
 * @deprecated for citation enrichment — the backend now sends enriched
 * citation metadata directly in the stream, so a citation chip must read the
 * fields it was given rather than joining against this catalogue.
 */
export function useQuerySources({ enabled = true }: SourcesReadOptions = {}) {
  return useQuery<ChatSourceItem[]>({
    queryKey: [GET_SOURCES],
    queryFn: getSources,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
